import React, { useState, useEffect } from 'react';
import { useVCOnboarding } from '../../../contexts/VCOnboardingContext';
import { VCFundInfo, FUND_SIZE_LABELS, FundSize } from '../../../types/vcOnboarding';
import styles from '../VCOnboarding.module.css';

const Step1FundInfo: React.FC = () => {
  const { data, saveStep, nextStep, loading } = useVCOnboarding();
  
  const [formData, setFormData] = useState<VCFundInfo>({
    fundName: '',
    fundSize: 'under_10m',
    website: '',
  });
  
  const [errors, setErrors] = useState<Partial<Record<keyof VCFundInfo, string>>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (data.step1_fund_info) {
      setFormData(data.step1_fund_info);
    }
  }, [data.step1_fund_info]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof VCFundInfo, string>> = {};
    
    if (!formData.fundName.trim()) {
      newErrors.fundName = 'Fund name is required';
    }
    
    if (!formData.website.trim()) {
      newErrors.website = 'Website is required';
    } else if (!/^https?:\/\/.+\..+/.test(formData.website)) {
      newErrors.website = 'Please enter a valid URL (e.g., https://yourfund.com)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      await saveStep(1, formData);
      nextStep();
    } catch (error) {
      console.error('Error saving step 1:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof VCFundInfo, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Tell us about your fund</h2>
        <p className={styles.cardDescription}>
          Basic information about your venture fund
        </p>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="fundName">
            Fund Name
          </label>
          <input
            id="fundName"
            type="text"
            className={styles.input}
            placeholder="e.g., Acme Ventures"
            value={formData.fundName}
            onChange={(e) => handleChange('fundName', e.target.value)}
            disabled={isSaving || loading}
          />
          {errors.fundName && (
            <div className={styles.errorMessage}>{errors.fundName}</div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="fundSize">
            Fund Size
          </label>
          <select
            id="fundSize"
            className={styles.select}
            value={formData.fundSize}
            onChange={(e) => handleChange('fundSize', e.target.value)}
            disabled={isSaving || loading}
          >
            {Object.entries(FUND_SIZE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="website">
            Website
          </label>
          <input
            id="website"
            type="url"
            className={styles.input}
            placeholder="https://yourfund.com"
            value={formData.website}
            onChange={(e) => handleChange('website', e.target.value)}
            disabled={isSaving || loading}
          />
          {errors.website && (
            <div className={styles.errorMessage}>{errors.website}</div>
          )}
        </div>

        <div className={styles.buttonGroup}>
          <button
            type="submit"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={isSaving || loading}
          >
            {isSaving ? (
              <>
                <span className={styles.loadingSpinner} />
                Saving...
              </>
            ) : (
              <>
                Continue
                <svg className={styles.buttonIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Step1FundInfo;

